// Hooks for /flowgate/selections/* (phase 2 — selection, two-stage validation,
// comments). Mirrors the legacy useGetSelectedVariants / first/second validate
// / comment hooks in src/api/result/result.ts so consumers read familiar.
//
// The workbench is shared: list, with-data, and stats return every selection
// on the result. created_by records who added each row. Mutating calls on a
// result that predates `__row_id` return 409 with code: "no_row_id";
// classifyFlowgateResultError maps those.

import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { del, get, patch, post } from '@api/httpClient';
import { APIError } from '@api/types';
import queryClient from '@api/queryClient';
import {
  type BulkCreateFlowgateSelectionsRequest,
  type BulkCreateFlowgateSelectionsResponse,
  type BulkDeleteFlowgateSelectionsRequest,
  type BulkDeleteFlowgateSelectionsResponse,
  type BulkValidateRequest,
  type BulkValidateResponse,
  type CreateFlowgateSelectionCommentRequest,
  type CreateFlowgateSelectionRequest,
  type FlowgateSelection,
  type FlowgateSelectionComment,
  type FlowgateSelectionsWithData,
  type FlowgateValidationStats,
  type FlowgateValidationStatus,
  type FlowgateVariantReporting,
  type RejectFlowgateSelectionRequest,
  type UpdateFlowgateSelectionCommentRequest,
} from '@api/flowgate/types';

// ── Query keys ────────────────────────────────────────────────────────────────

const SELECTIONS_LIST = (
  resultId: string,
  status?: FlowgateValidationStatus,
) => ['flowgate', 'selections', { result: resultId, status: status ?? null }];
const SELECTION = (selectionId: string) => [
  'flowgate',
  'selections',
  { id: selectionId },
];
const VALIDATION_STATS = (resultId: string) => [
  'flowgate',
  'selections',
  'validation-stats',
  { result: resultId },
];
const SELECTIONS_WITH_DATA = (resultId: string) => [
  'flowgate',
  'selections',
  'with-data',
  { result: resultId },
];
const COMMENTS = (selectionId: string) => [
  'flowgate',
  'selections',
  { id: selectionId },
  'comments',
];

// Invalidate everything that could be affected by a selection mutation. Cheap
// and safe; the set of relevant queries is small.
const invalidateSelectionScope = (resultId: string | undefined) => {
  if (!resultId) return;
  queryClient.invalidateQueries({
    queryKey: ['flowgate', 'selections'],
  });
  queryClient.invalidateQueries({
    queryKey: VALIDATION_STATS(resultId),
  });
  queryClient.invalidateQueries({
    queryKey: SELECTIONS_WITH_DATA(resultId),
  });
  queryClient.invalidateQueries({
    queryKey: ['flowgate', 'discussion', resultId],
  });
};

// ── List / detail / CRUD ──────────────────────────────────────────────────────

type UseFlowgateSelectionsArgs = {
  resultId?: string;
  status?: FlowgateValidationStatus;
};

export const useFlowgateSelections = ({
  resultId,
  status,
}: UseFlowgateSelectionsArgs) =>
  useQuery<FlowgateSelection[]>({
    queryKey: SELECTIONS_LIST(resultId || '', status),
    enabled: !!resultId,
    queryFn: async () => {
      if (!resultId) return [];
      const params = new URLSearchParams({ result: resultId });
      if (status) params.set('status', status);
      const resp = await get<FlowgateSelection[]>(
        `flowgate/selections/?${params.toString()}`,
      );
      return resp.data;
    },
  });

export const useFlowgateSelection = (selectionId?: string) =>
  useQuery<FlowgateSelection | null>({
    queryKey: SELECTION(selectionId || ''),
    enabled: !!selectionId,
    queryFn: async () => {
      if (!selectionId) return null;
      const resp = await get<FlowgateSelection>(
        `flowgate/selections/${selectionId}/`,
      );
      return resp.data;
    },
  });

export const useCreateFlowgateSelection = () =>
  useMutation<
    AxiosResponse<FlowgateSelection>,
    APIError,
    CreateFlowgateSelectionRequest
  >({
    mutationFn: (body) => post<FlowgateSelection>('flowgate/selections/', body),
    onSuccess: (_resp, variables) =>
      invalidateSelectionScope(variables.flowgate_result_id),
  });

export const useDeleteFlowgateSelection = () =>
  useMutation<
    AxiosResponse<void>,
    APIError,
    { selectionId: string; resultId?: string }
  >({
    mutationFn: ({ selectionId }) =>
      del<void>(`flowgate/selections/${selectionId}/`),
    onSuccess: (_resp, variables) =>
      invalidateSelectionScope(variables.resultId),
  });

// PATCH for variant_reporting + ad-hoc updates.
export const useUpdateFlowgateSelection = () =>
  useMutation<
    AxiosResponse<FlowgateSelection>,
    APIError,
    {
      selectionId: string;
      resultId?: string;
      body: { variant_reporting?: FlowgateVariantReporting | null };
    }
  >({
    mutationFn: ({ selectionId, body }) =>
      patch<FlowgateSelection>(`flowgate/selections/${selectionId}/`, body),
    onSuccess: (_resp, variables) =>
      invalidateSelectionScope(variables.resultId),
  });

// ── Validation transitions (single) ───────────────────────────────────────────

type ValidationActionArgs = {
  selectionId: string;
  resultId?: string;
  comment?: string;
};

export const useFlowgateSelectionFirstValidate = () =>
  useMutation<AxiosResponse<FlowgateSelection>, APIError, ValidationActionArgs>(
    {
      mutationFn: ({ selectionId, comment }) =>
        post<FlowgateSelection>(
          `flowgate/selections/${selectionId}/first-validate/`,
          comment ? { comment } : {},
        ),
      onSuccess: (_resp, variables) =>
        invalidateSelectionScope(variables.resultId),
    },
  );

export const useFlowgateSelectionSecondValidate = () =>
  useMutation<AxiosResponse<FlowgateSelection>, APIError, ValidationActionArgs>(
    {
      mutationFn: ({ selectionId, comment }) =>
        post<FlowgateSelection>(
          `flowgate/selections/${selectionId}/second-validate/`,
          comment ? { comment } : {},
        ),
      onSuccess: (_resp, variables) =>
        invalidateSelectionScope(variables.resultId),
    },
  );

export const useFlowgateSelectionReject = () =>
  useMutation<
    AxiosResponse<FlowgateSelection>,
    APIError,
    ValidationActionArgs & RejectFlowgateSelectionRequest
  >({
    mutationFn: ({
      selectionId,
      round_number: roundNumber,
      comment,
      variant_label: variantLabel,
    }) =>
      post<FlowgateSelection>(`flowgate/selections/${selectionId}/reject/`, {
        round_number: roundNumber,
        comment,
        ...(variantLabel ? { variant_label: variantLabel } : {}),
      }),
    onSuccess: (_resp, variables) =>
      invalidateSelectionScope(variables.resultId),
  });

export const useFlowgateSelectionResetValidation = () =>
  useMutation<
    AxiosResponse<FlowgateSelection>,
    APIError,
    { selectionId: string; resultId?: string }
  >({
    mutationFn: ({ selectionId }) =>
      post<FlowgateSelection>(
        `flowgate/selections/${selectionId}/reset-validation/`,
        {},
      ),
    onSuccess: (_resp, variables) =>
      invalidateSelectionScope(variables.resultId),
  });

// ── Bulk operations ──────────────────────────────────────────────────────────

export const useBulkCreateFlowgateSelections = () =>
  useMutation<
    AxiosResponse<BulkCreateFlowgateSelectionsResponse>,
    APIError,
    BulkCreateFlowgateSelectionsRequest
  >({
    mutationFn: (body) =>
      post<BulkCreateFlowgateSelectionsResponse>(
        'flowgate/selections/bulk-create/',
        body,
      ),
    onSuccess: (_resp, variables) =>
      invalidateSelectionScope(variables.flowgate_result_id),
  });

export const useBulkFirstValidate = () =>
  useMutation<
    AxiosResponse<BulkValidateResponse>,
    APIError,
    BulkValidateRequest & { resultId?: string }
  >({
    mutationFn: ({ selection_ids, comment }) =>
      post<BulkValidateResponse>('flowgate/selections/bulk-first-validate/', {
        selection_ids,
        ...(comment ? { comment } : {}),
      }),
    onSuccess: (_resp, variables) =>
      invalidateSelectionScope(variables.resultId),
  });

export const useBulkSecondValidate = () =>
  useMutation<
    AxiosResponse<BulkValidateResponse>,
    APIError,
    BulkValidateRequest & { resultId?: string }
  >({
    mutationFn: ({ selection_ids, comment }) =>
      post<BulkValidateResponse>('flowgate/selections/bulk-second-validate/', {
        selection_ids,
        ...(comment ? { comment } : {}),
      }),
    onSuccess: (_resp, variables) =>
      invalidateSelectionScope(variables.resultId),
  });

export const useBulkDeleteFlowgateSelections = () =>
  useMutation<
    AxiosResponse<BulkDeleteFlowgateSelectionsResponse>,
    APIError,
    BulkDeleteFlowgateSelectionsRequest & { resultId?: string }
  >({
    mutationFn: (variables) => {
      // Strip our local-only `resultId` field before sending to the BE.
      const body: BulkDeleteFlowgateSelectionsRequest =
        'selection_ids' in variables
          ? { selection_ids: variables.selection_ids }
          : {
              flowgate_result_id: variables.flowgate_result_id,
              row_ids: variables.row_ids,
            };
      return post<BulkDeleteFlowgateSelectionsResponse>(
        'flowgate/selections/bulk-delete/',
        body,
      );
    },
    onSuccess: (_resp, variables) =>
      invalidateSelectionScope(variables.resultId),
  });

// ── Stats + joined data ──────────────────────────────────────────────────────

export const useFlowgateValidationStats = (resultId?: string) =>
  useQuery<FlowgateValidationStats | null>({
    queryKey: VALIDATION_STATS(resultId || ''),
    enabled: !!resultId,
    queryFn: async () => {
      if (!resultId) return null;
      const resp = await get<FlowgateValidationStats>(
        `flowgate/selections/validation-stats/?result=${resultId}`,
      );
      return resp.data;
    },
  });

export const useFlowgateSelectionsWithData = (resultId?: string) =>
  useQuery<FlowgateSelectionsWithData | null>({
    queryKey: SELECTIONS_WITH_DATA(resultId || ''),
    enabled: !!resultId,
    queryFn: async () => {
      if (!resultId) return null;
      const resp = await get<FlowgateSelectionsWithData>(
        `flowgate/selections/with-data/?result=${resultId}`,
      );
      return resp.data;
    },
  });

// ── Comments ──────────────────────────────────────────────────────────────────

export const useFlowgateSelectionComments = (selectionId?: string) =>
  useQuery<FlowgateSelectionComment[]>({
    queryKey: COMMENTS(selectionId || ''),
    enabled: !!selectionId,
    queryFn: async () => {
      if (!selectionId) return [];
      const resp = await get<FlowgateSelectionComment[]>(
        `flowgate/selections/${selectionId}/comments/`,
      );
      return resp.data;
    },
  });

export const useCreateFlowgateSelectionComment = () =>
  useMutation<
    AxiosResponse<FlowgateSelectionComment>,
    APIError,
    {
      selectionId: string;
      resultId?: string;
      body: CreateFlowgateSelectionCommentRequest;
    }
  >({
    mutationFn: ({ selectionId, body }) =>
      post<FlowgateSelectionComment>(
        `flowgate/selections/${selectionId}/comments/`,
        body,
      ),
    onSuccess: (_resp, variables) => {
      queryClient.invalidateQueries({
        queryKey: COMMENTS(variables.selectionId),
      });
      invalidateSelectionScope(variables.resultId);
    },
  });

export const useUpdateFlowgateSelectionComment = () =>
  useMutation<
    AxiosResponse<FlowgateSelectionComment>,
    APIError,
    {
      selectionId: string;
      commentId: string;
      resultId?: string;
      body: UpdateFlowgateSelectionCommentRequest;
    }
  >({
    mutationFn: ({ selectionId, commentId, body }) =>
      patch<FlowgateSelectionComment>(
        `flowgate/selections/${selectionId}/comments/${commentId}/`,
        body,
      ),
    onSuccess: (_resp, variables) => {
      queryClient.invalidateQueries({
        queryKey: COMMENTS(variables.selectionId),
      });
      invalidateSelectionScope(variables.resultId);
    },
  });

export const useDeleteFlowgateSelectionComment = () =>
  useMutation<
    AxiosResponse<void>,
    APIError,
    { selectionId: string; commentId: string; resultId?: string }
  >({
    mutationFn: ({ selectionId, commentId }) =>
      del<void>(`flowgate/selections/${selectionId}/comments/${commentId}/`),
    onSuccess: (_resp, variables) => {
      queryClient.invalidateQueries({
        queryKey: COMMENTS(variables.selectionId),
      });
      invalidateSelectionScope(variables.resultId);
    },
  });
